import {
  useAppSelector,
  useDebounceValue,
  useStateWithSearchParams,
} from "../../../../hooks";
import { ReuseModal } from "../../ReuseModal";
import { Stack, Typography } from "@mui/material";
import { AccountsList } from "../GetMoreContactsForAccountsModal/AccountsList";
import { AccountPlanStatus } from "../../../../types/enums";
import { useGetAccountsPlanListQuery } from "../../../../api";
import { useParams } from "react-router-dom";
import { useState } from "react";

const formattedDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const d1 = startOfDay(date);
  const d2 = startOfDay(today);

  const diffMs = d2.getTime() - d1.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const isToday = diffDays === 0;

  if (isToday) return "Today";

  if (diffDays >= 1 && diffDays <= 6) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  const sameYear = date.getFullYear() === today.getFullYear();

  if (sameYear) {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  }

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  page?: string;
  per_page?: string;
  selected?: string;
  limit?: string;
  date?: string;
};

export const AccountPlanModal = ({
  onClose,
  onSubmit,
  isLoading,
  title,
  description,
  submitBtnText,
  status_filter,
}: {
  onClose: () => void;
  onSubmit: (selected: string[]) => void;
  isLoading: boolean;
  title: string;
  description: string;
  submitBtnText: string;
  status_filter: AccountPlanStatus;
}) => {
  const { campaign_id } = useParams();
  const campaignId = useAppSelector((state) => state.company.company);

  const [selected, setSelected] = useState<string[]>([]);

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    industries: undefined,
    regions: undefined,
    sizes: undefined,
    search: undefined,
    page: undefined,
    per_page: undefined,
    selected: undefined,
    limit: undefined,
    date: undefined,
  });

  const querySearchValueDebounce = useDebounceValue(searchDict.search || "");

  const { data: companies, isLoading: isCompaniesLoading } =
    useGetAccountsPlanListQuery(
      {
        campaign_id: campaign_id || campaignId,
        per_page: 1000,
        search: querySearchValueDebounce || undefined,
        status_filter,
      },
      { refetchOnMountOrArgChange: true },
    );

  const submitHandler = () => {
    onSubmit(selected);
  };

  return (
    <ReuseModal
      isOpen={true}
      isLoading={isCompaniesLoading || isLoading}
      onClose={onClose}
      title={title}
      onSubmit={submitHandler}
      maxWidth="460px"
      submitBtnColor="#1C3C6C"
      submitBtnText={submitBtnText}
      isDisabled={selected.length === 0 || isCompaniesLoading || isLoading}
      isDisabledCancelBtn={isLoading}
    >
      <Stack gap="16px">
        <Stack>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            fontWeight={500}
          >
            {description}
          </Typography>
          {companies && companies.items.length > 0 && (
            <AccountsList
              headerRightTitle={"Last Updated"}
              accounts={companies.items.map(
                ({ account_id, account_name, updated_at }) => ({
                  id: account_id,
                  name: account_name,
                  missed: 0,
                  value: (
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      color="custom.textDarkBlue"
                    >
                      {formattedDate(updated_at)}
                    </Typography>
                  ),
                }),
              )}
              selectedIds={selected}
              setSelectedIds={setSelected}
              withSelectAll
              withSort={false}
              withHeader={true}
              withMissed={false}
            />
          )}
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
